import { Navigate, type RouteObject } from "react-router-dom";

import { PartnerLayout } from "@/components/PartnerLayout";
import { PartnerRouteGuard } from "@/components/PartnerRouteGuard";
import { PlatformLayout } from "@/components/PlatformLayout";
import { PlatformRouteGuard } from "@/components/PlatformRouteGuard";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SupportConsoleLayout } from "@/components/SupportConsoleLayout";
import { SupportConsoleRouteGuard } from "@/components/SupportConsoleRouteGuard";
import { Spinner } from "@/components/Spinner";
import { isAuthenticated } from "@/lib/auth";
import { usePartnerMe } from "@/lib/partner";
import { usePlatformMe } from "@/lib/platform";
import { useAgentMe } from "@/lib/support";
import PartnerAddCommercialPage from "./pages/PartnerAddCommercialPage";
import PartnerCommercialsPage from "./pages/PartnerCommercialsPage";
import PartnerCommercialTeamPage from "./pages/PartnerCommercialTeamPage";
import PartnerEditCommercialPage from "./pages/PartnerEditCommercialPage";
import PartnerCommissionsPage from "./pages/PartnerCommissionsPage";
import PartnerOrganizationsPage from "./pages/PartnerOrganizationsPage";
import PartnerOrgFormPage from "./pages/PartnerOrgFormPage";
import PartnerJoinPage from "./pages/PartnerJoinPage";
import PartnerOverviewPage from "./pages/PartnerOverviewPage";
import PartnerPayoutsPage from "./pages/PartnerPayoutsPage";
import PartnerSettingsPage from "./pages/PartnerSettingsPage";
import PartnerTeamPage from "./pages/PartnerTeamPage";

import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SignupPage from "./pages/SignupPage";
import SsoCompletePage from "./pages/SsoCompletePage";
import InvitePage from "./pages/InvitePage";
import DashboardPage from "./pages/DashboardPage";
import PublicVerifyPage from "./pages/PublicVerifyPage";
import SessionAuditPage from "./pages/SessionAuditPage";
import SessionMediaPage from "./pages/SessionMediaPage";
import SessionPage from "./pages/SessionPage";
import SessionParticipantsPage from "./pages/SessionParticipantsPage";
import JoinPage from "./pages/JoinPage";
import AiLandingPage from "./pages/AiLandingPage";
import AdminApiKeysPage from "./pages/AdminApiKeysPage";
import AdminBillingPage from "./pages/AdminBillingPage";
import AdminBrandingPage from "./pages/AdminBrandingPage";
import AdminCustomFieldsPage from "./pages/AdminCustomFieldsPage";
import AdminDomainsPage from "./pages/AdminDomainsPage";
import AdminKybPage from "./pages/AdminKybPage";
import AdminMembersPage from "./pages/AdminMembersPage";
import AdminOperatorDetailPage from "./pages/AdminOperatorDetailPage";
import AdminOrgSettingsPage from "./pages/AdminOrgSettingsPage";
import AdminRetentionPage from "./pages/AdminRetentionPage";
import AccountProfilePage from "./pages/AccountProfilePage";
import ConfirmSessionPage from "./pages/ConfirmSessionPage";
import ContactDetailPage from "./pages/ContactDetailPage";
import ContactEditPage from "./pages/ContactEditPage";
import ContactsListPage from "./pages/ContactsListPage";
import AdminRolesPage from "./pages/AdminRolesPage";
import AdminSsoPage from "./pages/AdminSsoPage";
import AdminWebhooksPage from "./pages/AdminWebhooksPage";
import AuditDashboardPage from "./pages/AuditDashboardPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import CalendarPage from "./pages/CalendarPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import AICampaignPage from "./pages/AICampaignPage";
import CampaignImportsPage from "./pages/CampaignImportsPage";
import CampaignReportPage from "./pages/CampaignReportPage";
import CampaignsPage from "./pages/CampaignsPage";
import CampaignWizardPage from "./pages/CampaignWizardPage";
import NotFoundPage from "./pages/NotFoundPage";
import PlatformAuditEventsPage from "./pages/PlatformAuditEventsPage";
import PlatformLandingPage from "./pages/PlatformLandingPage";
import PlatformOrgDetailPage from "./pages/PlatformOrgDetailPage";
import PlatformOrgListPage from "./pages/PlatformOrgListPage";
import PlatformInvoicesPage from "./pages/PlatformInvoicesPage";
import PlatformPartnersPage from "./pages/PlatformPartnersPage";
import SessionsListPage from "./pages/SessionsListPage";
import SupportTicketsPage from "./pages/SupportTicketsPage";
import SupportPlanPage from "./pages/SupportPlanPage";
import SupportTicketDetailPage from "./pages/SupportTicketDetailPage";
import SupportQueuePage from "./pages/SupportQueuePage";
import SupportAgentTicketPage from "./pages/SupportAgentTicketPage";

// eslint-disable-next-line react-refresh/only-export-components -- routes table co-located with one trivial helper component; not worth a separate file.
function RootRedirect() {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return <PartnerAwareHome />;
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located redirect helper.
function PartnerAwareHome() {
  const partner = usePartnerMe();
  const platform = usePlatformMe();
  const agent = useAgentMe();
  if (partner.isLoading || platform.isLoading || agent.isLoading)
    return <Spinner fullscreen label="Loading…" />;
  if (partner.data) return <Navigate to="/partner" replace />;
  if (platform.data?.is_platform_staff)
    return <Navigate to="/platform" replace />;
  // Support-only staff (no org membership) land on the support console
  // instead of the customer "no organizations" dead-end.
  if (agent.data?.is_support_staff)
    return <Navigate to="/support-console" replace />;
  return <Navigate to="/dashboard" replace />;
}

export const routes: RouteObject[] = [
  { path: "/", element: <RootRedirect /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <SignupPage /> },
  // Password reset — public, unauthenticated. /forgot-password
  // captures an email and triggers a reset email; /reset-password
  // reads ?token=... from the link and accepts the new password.
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  // Phase 3-sso — IdP callback landing. Public route; consumes the
  // URL fragment, stashes tokens, redirects to /dashboard.
  { path: "/sso/complete", element: <SsoCompletePage /> },
  // Org/operator invite accept. The backend
  // (apps/accounts/services/invites.py) emits
  // `{FRONTEND_APP_URL}/invites/{token}` — plural, matching the
  // `invites/<token>/accept` API path. Field-user *session* invites are
  // a different flow and land on /join/:sessionId (below).
  { path: "/invites/:token", element: <InvitePage /> },
  // Partner-team invite accept (set password + auto-login). Public — the
  // signed token is the auth factor.
  { path: "/partner/join/:token", element: <PartnerJoinPage /> },
  // Phase D — beneficiary confirmation landing page. Public,
  // unauthenticated; signed token in ?t= is the only auth factor.
  { path: "/confirm/:sessionId", element: <ConfirmSessionPage /> },
  // Field-user join — unauthenticated, the signed token IS the auth factor.
  // Backend issues URLs of the form `/join/{session_id}?t={token}` (see
  // apps/sessions/services.py issue_invite); the SPA route mirrors that.
  // The api.ts request interceptor strips the Bearer header for any URL
  // matching /sessions/{id}/join/, so even an authed session won't leak it.
  { path: "/join/:sessionId", element: <JoinPage /> },
  // AI-campaign beneficiary landing — unauthenticated; the per-contact AI
  // tool token in the path IS the capability. "Start now" opens a live
  // session and hands off to /join; "pick a slot" books an appointment.
  { path: "/i/:token", element: <AiLandingPage /> },
  // Phase 2A-aud-pub — public auditor-verification page. Unauthenticated;
  // takes a `?t=<token>` query param from the shareable URL the
  // operator mints under Session → Audit chain → Verification links.
  {
    path: "/verify/:sessionId",
    element: <PublicVerifyPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: "/dashboard", element: <DashboardPage /> },
      { path: "/sessions", element: <SessionsListPage /> },
      { path: "/calendar", element: <CalendarPage /> },
      { path: "/sessions/:id", element: <SessionPage /> },
      { path: "/sessions/:id/audit", element: <SessionAuditPage /> },
      { path: "/sessions/:id/media", element: <SessionMediaPage /> },
      { path: "/sessions/:id/participants", element: <SessionParticipantsPage /> },
      { path: "/account", element: <AccountProfilePage /> },
      // R3 of CONTACTS_REFACTOR — top-level Beneficiaries / Contacts.
      { path: "/contacts", element: <ContactsListPage /> },
      { path: "/contacts/:id", element: <ContactDetailPage /> },
      { path: "/contacts/:id/edit", element: <ContactEditPage /> },
      { path: "/admin/settings", element: <AdminOrgSettingsPage /> },
      { path: "/admin/members", element: <AdminMembersPage /> },
      {
        path: "/admin/operators/:userId",
        element: <AdminOperatorDetailPage />,
      },
      { path: "/admin/roles", element: <AdminRolesPage /> },
      { path: "/admin/retention", element: <AdminRetentionPage /> },
      { path: "/admin/billing", element: <AdminBillingPage /> },
      { path: "/admin/kyb", element: <AdminKybPage /> },
      { path: "/admin/fields", element: <AdminCustomFieldsPage /> },
      { path: "/admin/sso", element: <AdminSsoPage /> },
      // Phase 3-pp admin surface.
      { path: "/admin/branding", element: <AdminBrandingPage /> },
      { path: "/admin/api-keys", element: <AdminApiKeysPage /> },
      { path: "/admin/webhooks", element: <AdminWebhooksPage /> },
      { path: "/admin/domains", element: <AdminDomainsPage /> },
      { path: "/audit", element: <AuditDashboardPage /> },
      { path: "/analytics", element: <AnalyticsDashboardPage /> },
      { path: "/campaigns", element: <CampaignsPage /> },
      { path: "/campaigns/new", element: <CampaignWizardPage /> },
      { path: "/campaigns/:id/wizard", element: <CampaignWizardPage /> },
      { path: "/campaigns/:id", element: <CampaignDetailPage /> },
      { path: "/campaigns/:id/imports", element: <CampaignImportsPage /> },
      { path: "/campaigns/:id/ai", element: <AICampaignPage /> },
      { path: "/campaigns/:id/report", element: <CampaignReportPage /> },
      // Support (customer surface): tickets + support plan + live calls.
      { path: "/support", element: <SupportTicketsPage /> },
      { path: "/support/plan", element: <SupportPlanPage /> },
      { path: "/support/tickets/:id", element: <SupportTicketDetailPage /> },
    ],
  },
  // Support-agent console (staff). Gated on /support/agent/me — support OR
  // platform staff with MFA. Separate from the /platform super-admin console.
  {
    element: <SupportConsoleRouteGuard />,
    children: [
      {
        element: <SupportConsoleLayout />,
        children: [
          { path: "/support-console", element: <SupportQueuePage /> },
          {
            path: "/support-console/tickets/:id",
            element: <SupportAgentTicketPage />,
          },
        ],
      },
    ],
  },
  // Reseller/commercial partner console. Gated on partner/me (a 403 for
  // non-partner users bounces to /dashboard). Partners have no org
  // membership; they create + manage their own customer orgs here.
  {
    element: <PartnerRouteGuard />,
    children: [
      {
        element: <PartnerLayout />,
        children: [
          { path: "/partner", element: <PartnerOverviewPage /> },
          { path: "/partner/team", element: <PartnerTeamPage /> },
          {
            path: "/partner/commercial/team",
            element: <PartnerCommercialTeamPage />,
          },
          {
            path: "/partner/organizations",
            element: <PartnerOrganizationsPage />,
          },
          {
            path: "/partner/organizations/new",
            element: <PartnerOrgFormPage />,
          },
          {
            path: "/partner/organizations/:orgId/edit",
            element: <PartnerOrgFormPage />,
          },
          {
            path: "/partner/commercials",
            element: <PartnerCommercialsPage />,
          },
          {
            path: "/partner/commercials/new",
            element: <PartnerAddCommercialPage />,
          },
          {
            path: "/partner/commercials/:id/edit",
            element: <PartnerEditCommercialPage />,
          },
          {
            path: "/partner/settings",
            element: <PartnerSettingsPage />,
          },
          {
            path: "/partner/commissions",
            element: <PartnerCommissionsPage />,
          },
          { path: "/partner/payouts", element: <PartnerPayoutsPage /> },
        ],
      },
    ],
  },
  // Phase 3-platform — cross-tenant super-admin console. Gated on
  // is_platform_staff + mfa_enabled. Separate from /admin/* (per-org).
  {
    element: <PlatformRouteGuard />,
    children: [
      {
        element: <PlatformLayout />,
        children: [
          { path: "/platform", element: <PlatformLandingPage /> },
          { path: "/platform/orgs", element: <PlatformOrgListPage /> },
          { path: "/platform/orgs/:id", element: <PlatformOrgDetailPage /> },
          { path: "/platform/partners", element: <PlatformPartnersPage /> },
          { path: "/platform/invoices", element: <PlatformInvoicesPage /> },
          { path: "/platform/audit", element: <PlatformAuditEventsPage /> },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
];